import { useEffect, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { StarInput, Stars } from "@/components/Stars";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const EditReviewDialog = ({ review, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (review) setF({ rating: review.rating, title: review.title || "", comment: review.comment || "", author_name: review.author_name });
  }, [review]);
  const save = async () => {
    try {
      const { data } = await api.patch(`/admin/reviews/${review.id}`, f);
      toast.success("Recensione modificata");
      onSaved(data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!review} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-review-dialog">
        <DialogHeader><DialogTitle>Modifica recensione</DialogTitle></DialogHeader>
        {f && (
          <div className="space-y-4">
            <StarInput size={26} value={f.rating} onChange={(rating) => setF({ ...f, rating })} testid="edit-rating" />
            <div className="space-y-1.5"><Label>Autore</Label><Input value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} data-testid="edit-author-input" /></div>
            <div className="space-y-1.5"><Label>Titolo</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="edit-title-input" /></div>
            <div className="space-y-1.5"><Label>Testo</Label><Textarea rows={6} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="edit-comment-input" /></div>
            <p className="text-xs text-gray-500">Le modifiche vengono registrate nel log attività.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel-button">Annulla</Button>
          <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={save} data-testid="edit-save-button">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

async function streamAiReply(id, onChunk) {
  const res = await fetch(`${API}/admin/reviews/${id}/ai-reply`, { method: "POST", credentials: "include" });
  if (!res.ok) throw new Error("Generazione non riuscita");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    onChunk(dec.decode(value, { stream: true }));
  }
}

export const ReplyDialog = ({ review, onClose, onSaved }) => {
  const [text, setText] = useState("");
  const [gen, setGen] = useState(false);
  useEffect(() => { if (review) setText(review.reply?.text || ""); }, [review]);

  const generate = async () => {
    setGen(true); setText("");
    try { await streamAiReply(review.id, (c) => setText((t) => t + c)); }
    catch (e) { toast.error(e.message); }
    finally { setGen(false); }
  };
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/reviews/${review.id}/reply`, { text });
      toast.success(text.trim() ? "Risposta salvata" : "Risposta rimossa");
      onSaved(data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!review} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="reply-dialog">
        <DialogHeader><DialogTitle>Rispondi pubblicamente</DialogTitle></DialogHeader>
        {review && (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm">
              <div className="flex items-center gap-2"><Stars value={review.rating} size={14} /><span className="font-semibold">{review.author_name}</span></div>
              <p className="mt-2 text-gray-700 line-clamp-4">{review.comment}</p>
            </div>
            <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi la risposta del negozio…" data-testid="reply-text-input" />
            <Button type="button" variant="outline" onClick={generate} disabled={gen} className="border-emerald-900/30 text-emerald-900" data-testid="reply-ai-generate-button">
              {gen ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} Genera risposta AI (Claude)
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reply-cancel-button">Annulla</Button>
          <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={save} disabled={gen} data-testid="reply-save-button">Pubblica risposta</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
